import { MessageBubble } from "@/components/chat/MessageBubble";
import { UserAvatar } from "@/components/UserAvatar";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
    ResizableHandle,
    ResizablePanel,
    ResizablePanelGroup,
} from "@/components/ui/resizable";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useAdminMessages } from "@/hooks/useAdminMessages";
import { userService } from "@/services/userService";
import { ChatMessage } from "@/types/chat";
import { cn } from "@/lib/utils";
import { Loader2, MessageSquare, Search, Sparkles } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";

interface UserOption {
    label: string;
    value: string;
    avatarUrl?: string;
}

export const ConversationsManagement = () => {
    const [selectedUserId, setSelectedUserId] = useState<string | undefined>(undefined);
    const [searchQuery, setSearchQuery] = useState("");
    
    const scrollAreaRef = useRef<HTMLDivElement>(null);
    const sentinelRef = useRef<HTMLDivElement>(null);
    const previousScrollHeightRef = useRef<number>(0);
    const previousScrollTopRef = useRef<number>(0);

    const {
        data: messagesData,
        isLoading,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
    } = useAdminMessages(selectedUserId);

    const [allUsers, setAllUsers] = useState<UserOption[]>([]);
    const [loadingUsers, setLoadingUsers] = useState(true);

    // Fetch users for the user list
    useEffect(() => {
        const fetchUsers = async () => {
            try {
                setLoadingUsers(true);
                const response = await userService.getCompanyUsers("", 1, 50);
                const options = response.data.map((user) => ({
                    label: user.fullName,
                    value: user.pid,
                    avatarUrl: user.avatarUrl,
                }));
                setAllUsers(options);
            } catch (error) {
                console.error("Failed to fetch users:", error);
            } finally {
                setLoadingUsers(false);
            }
        };
        fetchUsers();
    }, []);

    // Filter users based on search
    const filteredUsers = useMemo(() => {
        if (!searchQuery.trim()) return allUsers;
        const query = searchQuery.toLowerCase();
        return allUsers.filter((user) =>
            user.label.toLowerCase().includes(query)
        );
    }, [allUsers, searchQuery]);

    // Get selected user info
    const selectedUser = useMemo(() => {
        return allUsers.find((user) => user.value === selectedUserId);
    }, [allUsers, selectedUserId]);

    // Flatten paginated messages and sort chronologically
    const chatMessages: ChatMessage[] = useMemo(() => {
        if (!messagesData?.pages) return [];
        return messagesData.pages
            .flatMap((page) => page.data)
            .map((msg) => ({
                pid: msg.pid,
                role: msg.role,
                content: msg.content,
                createdAt: msg.createdAt,
                updatedAt: msg.updatedAt || msg.createdAt,
            }))
            .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
    }, [messagesData?.pages]);

    const totalMessages = messagesData?.pages[0]?.total || 0;

    // Auto-scroll to bottom on initial load
    useEffect(() => {
        if (scrollAreaRef.current && chatMessages.length > 0 && !isFetchingNextPage) {
            const scrollContainer = scrollAreaRef.current.querySelector(
                "[data-radix-scroll-area-viewport]"
            );
            if (scrollContainer && previousScrollHeightRef.current === 0) {
                scrollContainer.scrollTo({
                    top: scrollContainer.scrollHeight,
                    behavior: "auto",
                });
            }
        }
    }, [chatMessages.length, isFetchingNextPage]);

    // Capture scroll position before fetching older messages
    useEffect(() => {
        const scrollContainer = scrollAreaRef.current?.querySelector(
            "[data-radix-scroll-area-viewport]"
        ) as HTMLElement;

        if (scrollContainer && isFetchingNextPage) {
            previousScrollHeightRef.current = scrollContainer.scrollHeight;
            previousScrollTopRef.current = scrollContainer.scrollTop;
        }
    }, [isFetchingNextPage]);

    // Restore scroll position after older messages load
    useEffect(() => {
        const scrollContainer = scrollAreaRef.current?.querySelector(
            "[data-radix-scroll-area-viewport]"
        ) as HTMLElement;

        if (
            scrollContainer &&
            !isFetchingNextPage &&
            previousScrollHeightRef.current > 0
        ) {
            const newScrollHeight = scrollContainer.scrollHeight;
            const heightDifference = newScrollHeight - previousScrollHeightRef.current;
            scrollContainer.scrollTop = previousScrollTopRef.current + heightDifference;

            // Reset refs
            previousScrollHeightRef.current = 0;
            previousScrollTopRef.current = 0;
        }
    }, [messagesData?.pages?.length, isFetchingNextPage]);

    // Intersection Observer for infinite scroll (load older messages when scrolling up)
    useEffect(() => {
        if (!sentinelRef.current || !selectedUserId) return;

        const scrollContainer = scrollAreaRef.current?.querySelector(
            "[data-radix-scroll-area-viewport]"
        );
        if (!scrollContainer) return;

        const observer = new IntersectionObserver(
            (entries) => {
                const [entry] = entries;
                if (entry.isIntersecting && hasNextPage && !isFetchingNextPage) {
                    fetchNextPage();
                }
            },
            {
                root: scrollContainer,
                rootMargin: "200px 0px 0px 0px", // Trigger 200px before reaching the top
                threshold: 0,
            }
        );

        observer.observe(sentinelRef.current);

        return () => {
            observer.disconnect();
        };
    }, [fetchNextPage, hasNextPage, selectedUserId]);

    // Reset scroll refs when user changes
    useEffect(() => {
        previousScrollHeightRef.current = 0;
        previousScrollTopRef.current = 0;
    }, [selectedUserId]);

    return (
        <Card className="h-[calc(100vh-280px)] min-h-[500px] overflow-hidden shadow-lg">
            <ResizablePanelGroup direction="horizontal" className="h-full">
                {/* Left Panel - User List */}
                <ResizablePanel defaultSize={30} minSize={20} maxSize={40}>
                    <div className="flex h-full flex-col border-r">
                        {/* Header */}
                        <div className="border-b p-4">
                            <h2 className="text-lg font-semibold mb-3">Conversations</h2>
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                                <Input
                                    placeholder="Search employees..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    className="pl-9"
                                />
                            </div>
                        </div>

                        {/* User List */}
                        <ScrollArea className="flex-1">
                            <div className="p-2">
                                {loadingUsers ? (
                                    <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
                                        <Loader2 className="h-6 w-6 animate-spin mb-2" />
                                        <span className="text-sm">Loading users...</span>
                                    </div>
                                ) : filteredUsers.length === 0 ? (
                                    <div className="py-8 text-center text-sm text-muted-foreground">
                                        No users found
                                    </div>
                                ) : (
                                    filteredUsers.map((user) => (
                                        <button
                                            key={user.value}
                                            onClick={() => setSelectedUserId(user.value)}
                                            className={cn(
                                                "w-full flex items-center gap-3 rounded-lg p-3 text-left transition-colors",
                                                "hover:bg-muted/50",
                                                selectedUserId === user.value && "bg-primary/10 border border-primary/20"
                                            )}
                                        >
                                            <UserAvatar
                                                src={user.avatarUrl}
                                                name={user.label}
                                                className="h-10 w-10 border"
                                            />
                                            <div className="flex-1 min-w-0">
                                                <p className="font-medium truncate">{user.label}</p>
                                                <p className="text-xs text-muted-foreground">
                                                    Click to view conversation
                                                </p>
                                            </div>
                                        </button>
                                    ))
                                )}
                            </div>
                        </ScrollArea>
                    </div>
                </ResizablePanel>

                <ResizableHandle withHandle />

                {/* Right Panel - Chat View */}
                <ResizablePanel defaultSize={70}>
                    <div className="flex h-full flex-col">
                        {selectedUserId && selectedUser ? (
                            <>
                                {/* Chat Header */}
                                <div className="border-b bg-muted/30 p-4">
                                    <div className="flex items-center gap-3">
                                        <UserAvatar
                                            src={selectedUser.avatarUrl}
                                            name={selectedUser.label}
                                            className="h-10 w-10 border"
                                        />
                                        <div className="flex-1">
                                            <h3 className="font-semibold">{selectedUser.label}</h3>
                                            <p className="text-xs text-muted-foreground">
                                                {totalMessages} messages in conversation
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                {/* Chat Messages */}
                                <ScrollArea className="flex-1" ref={scrollAreaRef}>
                                    <div className="mx-auto max-w-3xl space-y-6 p-6">
                                        {isLoading ? (
                                            <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                                                <Loader2 className="h-8 w-8 animate-spin mb-3" />
                                                <span className="text-sm">Loading conversation...</span>
                                            </div>
                                        ) : chatMessages.length === 0 ? (
                                            <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                                                <MessageSquare className="h-12 w-12 mb-3 opacity-50" />
                                                <p className="text-sm font-medium">No messages yet</p>
                                                <p className="text-xs">This employee hasn't started a conversation with AI</p>
                                            </div>
                                        ) : (
                                            <>
                                                {/* Loading indicator for older messages */}
                                                {isFetchingNextPage && (
                                                    <div className="flex justify-center items-center py-3 gap-2">
                                                        <Loader2 className="h-4 w-4 animate-spin text-primary" />
                                                        <span className="text-sm text-muted-foreground">
                                                            Loading older messages...
                                                        </span>
                                                    </div>
                                                )}

                                                {/* Beginning of conversation marker */}
                                                {!hasNextPage && chatMessages.length > 0 && (
                                                    <div className="text-center py-3">
                                                        <span className="text-xs text-muted-foreground">
                                                            Beginning of conversation
                                                        </span>
                                                    </div>
                                                )}

                                                {/* Sentinel for infinite scroll */}
                                                <div ref={sentinelRef} className="h-1" />

                                                {/* Messages */}
                                                {chatMessages
                                                    .filter((m) => !(m.role === "assistant" && !m.content))
                                                    .map((message) => (
                                                        <MessageBubble
                                                            key={message.pid}
                                                            message={message}
                                                            showTimestamp={true}
                                                            employeeUser={{
                                                                fullName: selectedUser.label,
                                                                avatarUrl: selectedUser.avatarUrl,
                                                            }}
                                                        />
                                                    ))}
                                            </>
                                        )}
                                    </div>
                                </ScrollArea>
                            </>
                        ) : (
                            /* Empty State - No user selected */
                            <div className="flex h-full flex-col items-center justify-center p-8 text-muted-foreground">
                                <div className="mb-6 flex h-20 w-20 items-center justify-center rounded-2xl bg-gradient-to-br from-primary/10 to-primary/5">
                                    <Sparkles className="h-10 w-10 text-primary/60" />
                                </div>
                                <h3 className="mb-2 text-lg font-semibold text-foreground">
                                    Review Employee Conversations
                                </h3>
                                <p className="max-w-sm text-center text-sm">
                                    Select an employee from the list to view their conversation history with the AI assistant
                                </p>
                            </div>
                        )}
                    </div>
                </ResizablePanel>
            </ResizablePanelGroup>
        </Card>
    );
};
